/**
 * Cues you can follow without looking at the screen.
 *
 * The phone is held out at arm's length with its back to the room and its
 * screen towards a user who is turning their whole body and watching the room
 * rather than the handset. Every instruction the app gives during a scan is
 * therefore given to someone who is not reading it, which is why the progress
 * bar has never been able to fix a badly paced turn.
 *
 * So the coaching is audible. The awkward part is that the app is running a
 * matched-filter measurement through the same speaker and microphone, and any
 * sound it makes lands in the recording. Two properties keep the cues out of
 * the measurement, and both are worth stating because they are the reason the
 * frequencies below are not arbitrary.
 *
 * **They sit below the analysis band.** Mapping analyses 3.5 kHz to 16 kHz
 * (`irToLook`, and the scan sweep itself starts at 900 Hz). Every cue tone
 * here is under 550 Hz - at least 2.7 octaves below the lower corner of a
 * 4th-order Butterworth bandpass, run zero-phase, so better than 100 dB down
 * before the envelope is ever taken. A cue is not quiet in the recording; it
 * is absent from the part of the recording anything reads.
 *
 * **They are played in the gaps.** Never overlapping a chirp, for a reason
 * that survives the filter argument: a phone speaker driven near full scale
 * for the sweep is not linear, and adding a second tone on top of it produces
 * intermodulation products that land wherever they like, including in band.
 * Sound in the rest gap also arrives *before* the next chirp's direct blast,
 * which puts it at negative time in that look's deconvolved impulse response -
 * outside the window the range profile reads at either end.
 *
 * Haptics fire alongside where the platform has them. They are the secondary
 * channel and not the primary one: iOS Safari has never implemented
 * `navigator.vibrate`, and a vibrating motor is a broadband noise source
 * bolted to the same chassis as the microphone.
 */

/** Every cue tone lives below this, and the analysis starts at 3.5 kHz. */
export const CUE_MAX_HZ = 700;

/**
 * The longest a cue may be if it is to fit the rest gap between two chirps.
 *
 * The scan preset rests 130 ms, and a cue needs slack at both ends of that -
 * in front so it clears the chirp just fired, behind so it is comfortably over
 * before the next one starts. What is left is the budget every cue below is
 * written to, and the number the tests hold them to. Get this wrong and
 * nothing breaks loudly: `captureScan` skips any cue too long for the gap it
 * was given, so an over-long "turn faster" would simply never be heard.
 */
export const MAX_GAP_CUE_SEC = 0.095;

/** Cues fired between chirps, and so bound by `MAX_GAP_CUE_SEC`. */
export const GAP_CUES = ['beat', 'quarter', 'behind', 'ahead', 'tilt'] as const;

export type CueName =
  | 'count'
  | 'go'
  | 'beat'
  | 'quarter'
  | 'behind'
  | 'ahead'
  | 'tilt'
  | 'done';

interface Blip {
  hz: number;
  /** Seconds from the start of the cue. */
  offsetSec: number;
  durationSec: number;
  gain: number;
}

/**
 * The cue vocabulary.
 *
 * Five things have to be distinguishable by ear alone, from a phone at arm's
 * length, by someone who has never heard them before. So they are separated by
 * rhythm first and pitch second - a count of blips is legible where a semitone
 * is not - and the two corrections are made to sound like what they are
 * asking for: "behind" is three rapid rising blips, "ahead" is one long low
 * one.
 */
const CUES: Record<CueName, Blip[]> = {
  // The ready beat and the two end markers play in clear air - before the
  // recorder is connected, or after the last chirp - so they are the only
  // cues not squeezed into 95 ms.
  count: [{ hz: 330, offsetSec: 0, durationSec: 0.07, gain: 0.5 }],
  go: [{ hz: 494, offsetSec: 0, durationSec: 0.15, gain: 0.6 }],
  done: [
    { hz: 392, offsetSec: 0, durationSec: 0.07, gain: 0.45 },
    { hz: 262, offsetSec: 0.085, durationSec: 0.14, gain: 0.45 },
  ],

  /** The plain tick of the turn. Kept short and quiet: it fires 16 times. */
  beat: [{ hz: 392, offsetSec: 0, durationSec: 0.05, gain: 0.34 }],
  /** Quarter turns, so the beat carries position and not only rhythm. */
  quarter: [
    { hz: 392, offsetSec: 0, durationSec: 0.038, gain: 0.38 },
    { hz: 523, offsetSec: 0.05, durationSec: 0.04, gain: 0.38 },
  ],
  /** Rising and hurried, because it is asking for exactly that. */
  behind: [
    { hz: 440, offsetSec: 0, durationSec: 0.022, gain: 0.42 },
    { hz: 523, offsetSec: 0.03, durationSec: 0.022, gain: 0.42 },
    { hz: 622, offsetSec: 0.06, durationSec: 0.03, gain: 0.42 },
  ],
  /** One long low note. Nothing else in the vocabulary sits still like this. */
  ahead: [{ hz: 262, offsetSec: 0, durationSec: 0.09, gain: 0.4 }],
  /** An octave leap, for the one instruction that is about up and down. */
  tilt: [
    { hz: 330, offsetSec: 0, durationSec: 0.036, gain: 0.4 },
    { hz: 660, offsetSec: 0.048, durationSec: 0.042, gain: 0.4 },
  ],
};

/** Total length of a cue, so a caller can check it fits the gap it is for. */
export function cueDurationSec(name: CueName): number {
  return CUES[name].reduce((longest, b) => Math.max(longest, b.offsetSec + b.durationSec), 0);
}

const HAPTICS: Partial<Record<CueName, number | number[]>> = {
  count: 25,
  go: 90,
  beat: 18,
  quarter: [18, 60, 18],
  behind: [14, 40, 14, 40, 14],
  ahead: 110,
  tilt: [20, 50, 20, 50, 20],
  done: [40, 70, 90],
};

export interface CueTrackOptions {
  /** Master level for the whole cue track; 0 disables it. */
  volume?: number;
  haptics?: boolean;
}

export interface CueTrack {
  /** Schedule a cue against the audio context's own clock. */
  at(when: number, name: CueName): void;
  /** Fire one now. Used for the ready beat, where nothing is recording yet. */
  now(name: CueName): void;
  enabled: boolean;
  stop(): void;
}

export function createCueTrack(context: AudioContext, options: CueTrackOptions = {}): CueTrack {
  const volume = options.volume ?? 1;
  const useHaptics = options.haptics ?? true;
  const bus = context.createGain();
  bus.gain.value = volume;
  bus.connect(context.destination);

  const live: OscillatorNode[] = [];
  // Either channel on its own is a usable cue track, so the track is live if
  // either is: a user who has silenced the tones on a phone that vibrates is
  // asking for buzzes, not for nothing.
  let enabled = volume > 0 || useHaptics;

  function schedule(when: number, name: CueName): void {
    if (!enabled) return;
    const start = Math.max(when, context.currentTime);
    for (const blip of volume > 0 ? CUES[name] : []) {
      const oscillator = context.createOscillator();
      const envelope = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = Math.min(blip.hz, CUE_MAX_HZ);
      const from = start + blip.offsetSec;
      const to = from + blip.durationSec;
      // Ramped rather than gated. A hard edge on a tone is a click, and a
      // click is the one shape that is broadband enough to put energy in the
      // analysis band despite the tone being nowhere near it.
      const attack = Math.min(0.012, blip.durationSec * 0.4);
      envelope.gain.setValueAtTime(0.0001, from);
      envelope.gain.exponentialRampToValueAtTime(Math.max(blip.gain, 0.0002), from + attack);
      envelope.gain.exponentialRampToValueAtTime(0.0001, to);
      oscillator.connect(envelope).connect(bus);
      oscillator.start(from);
      oscillator.stop(to + 0.01);
      live.push(oscillator);
      oscillator.onended = (): void => {
        const at = live.indexOf(oscillator);
        if (at >= 0) live.splice(at, 1);
        oscillator.disconnect();
        envelope.disconnect();
      };
    }
    if (useHaptics) buzz(name, (start - context.currentTime) * 1000);
  }

  return {
    at: schedule,
    now: (name) => schedule(context.currentTime + 0.01, name),
    get enabled(): boolean {
      return enabled;
    },
    set enabled(value: boolean) {
      enabled = value && (volume > 0 || useHaptics);
    },
    stop(): void {
      for (const oscillator of [...live]) {
        try {
          oscillator.stop();
        } catch {
          /* already stopped */
        }
      }
      live.length = 0;
      bus.disconnect();
    },
  };
}

/**
 * Vibrate, if this browser does it at all.
 *
 * Scheduled with a timer rather than the audio clock because there is no
 * other way to schedule it, which is a second reason it is the secondary
 * channel: the tone lands where it was told to and the buzz lands roughly
 * there.
 */
function buzz(name: CueName, delayMs: number): void {
  const pattern = HAPTICS[name];
  if (!pattern) return;
  if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return;
  const fire = (): void => {
    try {
      navigator.vibrate(pattern);
    } catch {
      /* some browsers expose it and refuse it */
    }
  };
  if (delayMs <= 8) fire();
  else setTimeout(fire, delayMs);
}
